(() => {
  if (customElements.get('pp-search-v2')) return;
  const parser = new DOMParser();
  const responseCache = new Map();
  const catalogs = new Map();
  const normalized = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const tokens = value => normalized(value).split(' ').filter(Boolean);
  const abortError = () => new DOMException('Aborted', 'AbortError');
  const sameOrigin = value => {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid search URL');
    return url;
  };
  const preview = url => { url.searchParams.delete('preview_theme_id'); return url; };
  async function fetchHTML(url, signal) {
    if (signal?.aborted) throw abortError();
    const key = url.toString();
    const cached = responseCache.get(key);
    if (cached && Date.now() - cached.time < 30000) return parser.parseFromString(cached.html, 'text/html');
    const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Search ${response.status}`);
    const html = await response.text();
    if (signal?.aborted) throw abortError();
    responseCache.set(key, { time: Date.now(), html });
    if (responseCache.size > 24) responseCache.delete(responseCache.keys().next().value);
    return parser.parseFromString(html, 'text/html');
  }
  async function loadCatalog(base) {
    const key = `${base}:${new URL(location.href).searchParams.get('preview_theme_id') || ''}`;
    const cached = catalogs.get(key);
    if (cached && Date.now() - cached.time < 300000) return cached.promise;
    const promise = (async () => {
      const byId = new Map();
      async function pageItems(page) {
        const url = preview(sameOrigin(base));
        url.searchParams.set('section_id', 'pp-search-v2-catalog');
        url.searchParams.set('page', String(page));
        const doc = await fetchHTML(url);
        const root = doc.querySelector('[data-pp2-catalog]');
        if (!root || Number(root.dataset.page) !== page) throw new Error('Incomplete collection catalog');
        root.querySelectorAll('[data-pp2-collection]').forEach(item => {
          const id = item.dataset.id;
          const title = item.dataset.title || item.textContent.trim();
          byId.set(id, { id, title, count: Number(item.dataset.count) || 0, words: tokens(`${title} ${item.dataset.handle}`), phrase: normalized(title), element: item });
        });
        return Math.max(1, Number(root.dataset.pages) || 1);
      }
      const pages = await pageItems(1);
      let nextPage = 2;
      await Promise.all(Array.from({ length: Math.min(3, pages - 1) }, async () => {
        while (nextPage <= pages) { const page = nextPage++; await pageItems(page); }
      }));
      return [...byId.values()];
    })();
    catalogs.set(key, { time: Date.now(), promise });
    promise.catch(() => { if (catalogs.get(key)?.promise === promise) catalogs.delete(key); });
    return promise;
  }
  function distance(a, b) {
    let row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
      const next = [i];
      for (let j = 1; j <= b.length; j += 1) next[j] = Math.min(next[j - 1] + 1, row[j] + 1, row[j - 1] + Number(a[i - 1] !== b[j - 1]));
      row = next;
    }
    return row[b.length];
  }
  function popularCollections(catalog) {
    // Follow the header's configured menu and visible order, within the allowed catalog.
    const path = href => new URL(href, location.origin).pathname.replace(/\/+$/, '');
    const byPath = new Map(catalog.map(item => [path(item.element.getAttribute('href')), item]));
    const seen = new Set();
    return [...document.querySelectorAll('.popular-collections__list .popular-collections__btn[href]')].flatMap(link => {
      const item = byPath.get(path(link.getAttribute('href')));
      if (!item || seen.has(item.id)) return [];
      seen.add(item.id);
      return [item];
    });
  }
  function rankCollections(catalog, query, nativeIds) {
    const words = tokens(query);
    const phrase = normalized(query);
    if (!phrase) return popularCollections(catalog);
    const scored = catalog.map(item => {
      const nativeIndex = nativeIds.indexOf(item.id);
      let score = nativeIndex >= 0 ? 500 - nativeIndex : 0;
      if (item.phrase === phrase) score += 2000;
      else if (item.phrase.startsWith(phrase)) score += 1000;
      else if (item.phrase.includes(phrase)) score += 700;
      let matched = 0;
      for (const word of words) {
        if (item.words.some(candidate => candidate === word || (!/^\d+$/.test(word) && candidate.startsWith(word)))) { matched += 1; score += 60; }
        else if (word.length >= 5 && !/\d/.test(word) && item.words.some(candidate => Math.abs(candidate.length - word.length) <= 2 && distance(word, candidate) <= (word.length >= 8 ? 2 : 1))) { matched += 1; score += 25; }
      }
      if (nativeIndex < 0 && matched !== words.length && score < 700) return null;
      if (!score || (!words.length && nativeIndex < 0)) return null;
      return { item, score };
    }).filter(Boolean);
    scored.sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title, 'nl'));
    return scored.map(result => result.item);
  }
  class PrettyPolishSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.lifecycle = new AbortController();
      this.mode = this.dataset.mode;
      this.messages = this.querySelector('[data-pp2-messages]').dataset;
      this.input = this.querySelector('[data-pp2-input]');
      this.status = this.querySelector('[data-pp2-status]');
      this.collectionList = this.querySelector('[data-pp2-collections]');
      this.collectionRegion = this.querySelector('[data-pp2-collections-region]');
      this.productList = this.querySelector('[data-pp2-products]');
      this.productsRegion = this.querySelector('[data-pp2-products-region]');
      this.results = this.querySelector('[data-pp2-results]');
      this.version = 0;
      this.catalogBase = this.dataset.catalogUrl;
      this.on(this.querySelector('[data-pp2-form]'), 'submit', event => {
        if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
        else { event.preventDefault(); location.assign(this.searchURL(this.input.value).toString()); }
      });
      if (this.mode === 'dialog') this.initDialog();
      else this.initPage();
    }
    disconnectedCallback() { this.cancel(); if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.previousOverflow || ''; } this.lifecycle?.abort(); this.ready = false; }
    on(element, type, fn) { element?.addEventListener(type, fn, { signal: this.lifecycle.signal }); }
    cancel() { clearTimeout(this.timer); this.searchController?.abort(); this.version += 1; }
    searchURL(query) {
      const url = preview(sameOrigin(this.dataset.searchUrl));
      url.searchParams.set('q', String(query || '').trim());
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'last');
      return url;
    }
    async nativeCollections(query, signal) {
      const url = this.searchURL(query);
      url.pathname = url.pathname.replace(/\/$/, '') + '/suggest';
      url.searchParams.delete('type');
      url.searchParams.set('resources[type]', 'collection');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('section_id', 'pp-search-v2-suggestions');
      const doc = await fetchHTML(url, signal);
      return [...doc.querySelectorAll('[data-pp2-native-collections] [data-id]')].map(item => item.dataset.id);
    }
    async showCollections(query, signal, version) {
      const native = query.length >= 2 ? this.nativeCollections(query, signal).catch(error => { if (error.name === 'AbortError') throw error; return []; }) : Promise.resolve([]);
      const [catalog, ids] = await Promise.all([loadCatalog(this.catalogBase), native]);
      if (signal?.aborted || this.version !== version) return 0;
      const matches = rankCollections(catalog, query, ids);
      this.collectionMatches = matches;
      this.collectionList.replaceChildren();
      const initial = this.mode === 'dialog' ? 6 : 12;
      matches.slice(0, initial).forEach(item => this.collectionList.append(this.collectionNode(item)));
      this.collectionRegion.hidden = matches.length === 0;
      this.querySelector('[data-pp2-collection-title]').textContent = query ? this.messages.collections : this.messages.popular;
      this.collectionRegion.querySelector('[data-pp2-more-collections]')?.remove();
      if (matches.length > initial) {
        const more = document.createElement('button');
        more.type = 'button'; more.className = 'pp2-more-collections'; more.dataset.pp2MoreCollections = '';
        more.textContent = (query ? this.messages.collections : this.messages.popular) + ` (${matches.length})`;
        this.on(more, 'click', () => {
          matches.slice(initial).forEach(item => this.collectionList.append(this.collectionNode(item)));
          more.remove();
        });
        this.collectionRegion.append(more);
      }
      return matches.length;
    }
    collectionNode(item) {
      const node = item.element.cloneNode(true);
      node.className = 'pp2-collection';
      const url = preview(sameOrigin(node.getAttribute('href')));
      node.href = url.toString();
      return node;
    }
    async batch(url, signal) {
      url = preview(sameOrigin(url));
      url.searchParams.set('section_id', this.mode === 'dialog' ? 'pp-search-v2-preview' : 'pp-search-v2-results');
      const doc = await fetchHTML(url, signal);
      const batch = doc.querySelector('[data-pp2-batch]');
      if (!batch) throw new Error('Missing search result');
      let next = batch.dataset.next;
      if (next) {
        const nextURL = sameOrigin(next);
        url.searchParams.forEach((value, key) => { if (key !== 'page' && !nextURL.searchParams.has(key)) nextURL.searchParams.set(key, value); });
        nextURL.searchParams.set('section_id', this.mode === 'dialog' ? 'pp-search-v2-preview' : 'pp-search-v2-results');
        next = nextURL.toString();
      }
      return { cards: [...batch.querySelectorAll('.pp2-products > li')], next };
    }
    initDialog() {
      this.dialog = this.querySelector('[data-pp2-dialog]');
      this.trigger = this.querySelector('[data-pp2-trigger]');
      this.viewAll = this.querySelector('[data-pp2-view-all]');
      this.on(this.trigger, 'click', () => this.open());
      this.on(this.trigger, 'keydown', event => { if (event.key === 'ArrowDown') { event.preventDefault(); this.open(); } });
      this.on(this.trigger, 'input', () => this.open());
      this.on(this.querySelector('[data-pp2-header-form]'), 'submit', event => { event.preventDefault(); if (!this.trigger.value.trim()) this.open(); else location.assign(this.searchURL(this.trigger.value.trim()).toString()); });
      this.on(this.querySelector('[data-pp2-close]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      });
      this.on(this.dialog, 'close', () => {
        this.cancel(); document.body.style.overflow = this.previousOverflow || '';
        this.trigger.setAttribute('aria-expanded', 'false');
        this.restoringFocus = true;
        this.trigger.focus({ preventScroll: true });
        queueMicrotask(() => { this.restoringFocus = false; });
      });
      this.on(this.input, 'input', event => { if (!event.isComposing) this.schedule(); });
      this.on(this.input, 'compositionend', () => this.schedule());
      this.on(this.input, 'keydown', event => {
        if (event.key === 'ArrowDown') {
          const first = this.results.querySelector('a');
          if (first) { event.preventDefault(); first.focus(); }
        }
      });
    }
    open() {
      if (this.dialog.open) return;
      this.previousOverflow = document.body.style.overflow;
      this.input.value = this.trigger.value;
      this.dialog.showModal(); document.body.style.overflow = 'hidden';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
      this.schedule(0);
    }
    schedule(delay = 220) {
      this.cancel();
      this.trigger.value = this.input.value;
      const query = this.input.value.trim();
      this.productList.replaceChildren(); this.productsRegion.hidden = true;
      this.collectionList.replaceChildren(); this.collectionRegion.hidden = true;
      this.viewAll.hidden = !query;
      this.viewAll.href = this.searchURL(query).toString();
      this.showHelp(false);
      this.status.textContent = query.length < 2 ? this.messages.short : this.messages.loading;
      this.timer = setTimeout(() => this.run(query), delay);
    }
    async run(query) {
      const version = this.version;
      const controller = new AbortController(); this.searchController = controller;
      const signal = controller.signal;
      this.results.setAttribute('aria-busy', 'true');
      const collectionJob = this.showCollections(query.length >= 2 ? query : '', signal, version);
      let products = 0;
      let productError = false;
      const productJob = (async () => {
        if (query.length < 2) return;
        let next = this.searchURL(query).toString();
        const seen = new Set();
        while (next && products < 8) {
          const batch = await this.batch(next, signal);
          if (signal.aborted || this.version !== version) throw abortError();
          batch.cards.forEach(card => {
            const id = card.dataset.productId;
            if (seen.has(id) || products >= 8) return;
            seen.add(id); this.productList.append(card); products += 1;
          });
          this.productsRegion.hidden = products === 0;
          next = batch.next;
        }
      })().catch(error => { if (error.name === 'AbortError') throw error; productError = true; });
      const results = await Promise.allSettled([collectionJob, productJob]);
      if (signal.aborted || this.version !== version) return;
      this.results.removeAttribute('aria-busy');
      const collectionError = results[0].status === 'rejected';
      const count = results[0].status === 'fulfilled' ? results[0].value : 0;
      if (productError) this.status.textContent = this.messages.error;
      else if (collectionError) this.status.textContent = this.messages.collectionsError;
      else if (query.length < 2) this.status.textContent = this.messages.short;
      else if (!products && !count) this.status.textContent = this.messages.empty;
      else this.status.textContent = this.messages.shown.replace('__COUNT__', String(products));
      if (!productError && !collectionError && query.length >= 2 && !products) this.showHelp(true, query, !count);
    }
    showHelp(show, query = '', popular = false) {
      const help = this.querySelector('[data-pp2-help]');
      if (!help) return;
      help.hidden = !show;
      if (!show) return;
      const links = help.querySelector('[data-pp2-help-links]');
      links.replaceChildren();
      const current = new URL(location.href);
      if (this.mode === 'page' && [...current.searchParams.keys()].some(key => key.startsWith('filter.'))) {
        const link = document.createElement('a'); link.href = this.searchURL(query); link.textContent = this.messages.clearFilters; links.append(link);
      }
      if (popular) loadCatalog(this.catalogBase).then(catalog => {
        if (help.hidden || this.querySelector('[data-pp2-input]')?.value.trim() !== query && this.mode === 'dialog') return;
        popularCollections(catalog).slice(0, 4).forEach(item => {
          const link = document.createElement('a'); link.href = item.element.href; link.textContent = item.title; links.append(link);
        });
      }).catch(() => {});
    }
    initPage() {
      const query = (this.dataset.query || '').trim();
      const controller = new AbortController(); this.searchController = controller;
      this.showCollections(query, controller.signal, this.version).catch(error => { if (error.name !== 'AbortError') this.collectionRegion.hidden = true; });
      const initial = this.querySelector('[data-pp2-batch]');
      if (!initial) return;
      this.productList = initial.querySelector('.pp2-products');
      this.seenProducts = new Set([...this.productList.children].map(card => card.dataset.productId));
      this.nextPage = initial.dataset.next ? sameOrigin(initial.dataset.next).toString() : '';
      this.more = initial.querySelector('[data-pp2-next]');
      if (!this.more) { this.more = document.createElement('button'); this.more.type = 'button'; this.more.className = 'pp2-more'; initial.append(this.more); }
      this.on(this.more, 'click', event => { event.preventDefault(); this.fillPage(this.productList.children.length + 24); });
      this.updatePageStatus();
      if (this.productList.children.length < 24 && this.nextPage) this.fillPage(24);
      if (matchMedia('(max-width: 760px)').matches) this.querySelector('.pp2-filters')?.removeAttribute('open');
    }
    updatePageStatus() {
      const count = this.productList.children.length;
      this.status.textContent = count ? this.messages.shown.replace('__COUNT__', String(count)) : (this.nextPage ? this.messages.loading : this.messages.empty);
      this.more.hidden = !this.nextPage;
      this.more.textContent = this.messages.more;
      this.showHelp(!count && !this.nextPage, this.dataset.query || '', !this.collectionMatches?.length);
      if (this.more.tagName === 'A' && this.nextPage) {
        const url = sameOrigin(this.nextPage); url.searchParams.delete('section_id'); this.more.href = url.toString();
      }
    }
    async fillPage(target) {
      if (this.filling) return;
      this.filling = true;
      this.more.setAttribute('aria-disabled', 'true');
      this.more.textContent = this.messages.loading;
      this.results.setAttribute('aria-busy', 'true');
      try {
        while (this.nextPage && this.productList.children.length < target) {
          const url = sameOrigin(this.nextPage);
          const current = new URL(location.href);
          current.searchParams.forEach((value, key) => { if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key)) url.searchParams.set(key, value); });
          const batch = await this.batch(url.toString(), this.searchController.signal);
          batch.cards.forEach(card => { if (!this.seenProducts.has(card.dataset.productId)) { this.seenProducts.add(card.dataset.productId); this.productList.append(card); } });
          this.nextPage = batch.next;
        }
        this.updatePageStatus();
      } catch (error) {
        if (error.name !== 'AbortError') { this.status.textContent = this.messages.error; this.more.textContent = this.messages.retry; this.more.hidden = false; }
      } finally { this.filling = false; this.more.removeAttribute('aria-disabled'); this.results.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('pp-search-v2', PrettyPolishSearch);
})();
